import { useTranslation } from "react-i18next";
import { type FieldValue } from "~/components/job_posts/field";
import { useUrlToken } from "~/hooks/use_url_token";
import type { JobPost, Question } from "~/types/jben/job_post";
import { Autofiller } from "../autofill/autofiller";
import { useEffect, useMemo, useRef } from "react";
import { buildMonthOptions, type EducationAnswer } from "~/components/job_posts/education_section";
import { EducationAutofiller } from "../autofill/education_autofiller";

export const useAutofillerRef = (
  answers: Record<string, FieldValue>,
  educationAnswers: EducationAnswer[],
  jobPost: JobPost,
  questions: Question[],
  setAnswers: (answers: Record<string, FieldValue>) => void,
  setEducationAnswers: (answers: EducationAnswer[]) => void
) => {
  const urlToken = useUrlToken();
  const { t } = useTranslation("job_post");
  const monthOptions = useMemo(() => buildMonthOptions(t), [t]);

  const memoizedAutofiller = useMemo(
    () =>
      new Autofiller(
        answers,
        setAnswers,
        questions,
        new EducationAutofiller(
          urlToken,
          educationAnswers,
          setEducationAnswers,
          monthOptions,
          jobPost.education_config
        )
      ),
    [
      answers,
      educationAnswers,
      monthOptions,
      questions,
      setAnswers,
      setEducationAnswers,
      urlToken,
      jobPost.education_config,
    ]
  );

  const autofillerRef = useRef<Autofiller>(memoizedAutofiller);

  useEffect(() => {
    autofillerRef.current = memoizedAutofiller;
  }, [memoizedAutofiller]);

  return autofillerRef;
};
